// src/services/pdf/index.ts
// Public API for the GP Summary PDF service.
// Usage: import { generateGPSummaryPDF } from "@/services/pdf";

import type { GPSummaryData } from "../../types/gpSummary";
import type { PDFSectionToggles } from "./types";
import { GPSummaryPDFGenerator } from "./GPSummaryPDFGenerator";
import { mapSummaryToPDFData } from "./data/mapSummaryToPDFData";
import { fetchPWResponses } from "./data/fetchPWResponses";

export type { PDFSectionToggles, PWQuestionnaireResponse, ClinicalFlag, GPAction } from "./types";

export interface GeneratePDFOptions {
  /** The full GP summary data from the Cloud Function */
  summaryData: GPSummaryData;
  /** Section toggle state from the Export Card UI */
  toggles: PDFSectionToggles;
  /** User's notes (verbatim, "In her own words") */
  notes: string;
  /** Firebase user UID — needed to fetch PW questionnaire responses */
  uid: string;
  /** Optional custom filename */
  filename?: string;
}

/**
 * Main entry point for PDF generation.
 * Fetches supplementary data (PW questionnaire responses), computes flags & actions,
 * and generates a clinical letter PDF that the user can bring to their GP.
 *
 * @returns void — triggers a browser download
 */
export async function generateGPSummaryPDF(options: GeneratePDFOptions): Promise<void> {
  const { summaryData, toggles, notes, uid, filename } = options;

  // Fetch PW video questionnaire responses (supplementary data)
  const pwResponses = await fetchPWResponses(uid);

  // Map all data into the PDF render structure
  const renderData = mapSummaryToPDFData({
    summaryData,
    pwResponses,
    notes,
    toggles,
  });

  // Generate and download
  const generator = new GPSummaryPDFGenerator();
  generator.generateAndDownload(renderData, filename);
}

/**
 * Generate the PDF as a Blob (useful for preview or sharing).
 */
export async function generateGPSummaryPDFBlob(options: GeneratePDFOptions): Promise<Blob> {
  const { summaryData, toggles, notes, uid } = options;

  const pwResponses = await fetchPWResponses(uid);

  const renderData = mapSummaryToPDFData({
    summaryData,
    pwResponses,
    notes,
    toggles,
  });

  const generator = new GPSummaryPDFGenerator();
  const doc = generator.generate(renderData);
  return doc.getBlob();
}
